import React, {useEffect, useMemo, useState}                 from 'react'
import {
	Box,
	CircularProgress,
	Container,
	InputAdornment,
	TextField,
	Typography,
	useTheme,
}                                                            from '@mui/material'
import {alpha, darken}                                       from '@mui/material/styles'
import {ArrowBack, Search}                                   from '@mui/icons-material'
import {useNavigate, useParams}                              from 'react-router-dom'
import {Helmet}                                              from 'react-helmet-async'
import {doGet}                                               from '../../util/do-fetch'
import {logger}                                              from '../../util/log-utils'
import CategoryIcon                                          from '../CategoryIcon'

const log = logger('Category', 1)

/*
 * /prep/:categoryToken — the PrepKits inside one category (163).
 *
 * Used to be a redirect straight out to the marketing site. It now lists what is in the category and
 * sends people onward per PREPKIT rather than per category, so the last choice made in the app is
 * the specific exam rather than the subject area.
 *
 * A card carries the name and two actions, nothing else (164, 165). The taglines on these records
 * all run to a full sentence of marketing copy — "Includes Alaska State, National, Math and
 * Vocabulary…" — near-identical across every PrepKit in a category, because the kits differ by
 * state rather than by content. The name is the only thing that distinguishes one from the next.
 *
 * The list is already limited to sellable kits by the API: fetchPackagesOnly filters to
 * `archived_at IS NULL` and `published_at IS NOT NULL`, so nothing draft or retired reaches here.
 *
 * Built from the same pieces as /prep and the prepkit dashboard: bg.surface cards, one hairline,
 * radius.lg, shadow.card, hover lift. The category's gradient appears ONCE, in the header tile — the
 * cards below are all in the same category, so repeating its colour on each of them would say
 * nothing and drown the names.
 */

/*
 * One PrepKit: its name, and the two things you can do with it (165, 166).
 *
 * The card is not itself a control. With one action it was a single <button>; two actions cannot work
 * that way, because a click on the card would have to resolve to one of them and either choice is
 * wrong half the time. So it is a plain container holding two real buttons.
 *
 * It still ANIMATES on hover — the lift and the category-tinted border from the category page (162),
 * so the two pages feel like one set. What the hover also does is fill Try Free with the category's
 * colour, which is why the card is the thing listening: hovering anywhere on the card lights up the
 * action it wants you to take, and at rest the grid stays quiet.
 */
const PrepKitCard = ({pkg, accent, accentText, custom, onTry, onDetails}) => (
	<Box
		sx={{
			display: 'flex',
			flexDirection: 'column',
			gap: 1.5,
			p: 2.25,
			background: custom.bg.surface,
			border: `1px solid ${custom.border.color}`,
			borderRadius: `${custom.radius.lg}px`,
			boxShadow: custom.shadow.card,
			transition: 'transform 150ms, box-shadow 150ms, border-color 150ms',
			'&:hover': {
				transform: 'translateY(-2px)',
				boxShadow: custom.shadow.pop,
				borderColor: alpha(accent, 0.5),
			},
			// the card's hover is what colours the primary action, so the fill follows the cursor
			// rather than sitting lit on all fifty cards at once
			'&:hover .prepkit-try': {
				background: accent,
				borderColor: accent,
				color: accentText,
			},
		}}
	>
		<Typography sx={{
			fontSize: custom.fontSize.lg,
			fontWeight: custom.fontWeight.bold,
			color: custom.text.main,
			lineHeight: 1.3,
		}}>
			{pkg.name}
		</Typography>

		<Box sx={{mt: 'auto', display: 'flex', alignItems: 'center', gap: 1}}>
			{/*
			  * Try Free takes the category's colour on hover; Details stays quiet throughout.
			  *
			  * The hierarchy is doing the work that the wording cannot. A category holds up to fifty of
			  * these, so two full-strength buttons per card would be a page of competing pitches — one
			  * filled button and one recessed one reads as a single offer repeated, which is what it is.
			  *
			  * aria-label names the PrepKit on both. Unlabelled, a screen reader lists this page as
			  * "Try Free, Details, Try Free, Details…" fifty times with nothing to tell them apart.
			  */}
			<Box
				component="button"
				type="button"
				className="prepkit-try"
				data-ga="prepkit-try"
				aria-label={`Try ${pkg.name} free`}
				onClick={() => onTry(pkg)}
				sx={{
					font: 'inherit',
					px: 2,
					py: 0.75,
					cursor: 'pointer',
					borderRadius: `${custom.radius.sm}px`,
					background: custom.bg.raised,
					/*
					 * At rest the only thing separating this from Details is the accent border. It has
					 * to be something: with the fill gone the two buttons would otherwise be identical,
					 * and the whole point of the pair is that one of them is the offer.
					 *
					 * The label stays on text.main rather than going accent-coloured. Accent-on-surface
					 * is the tempting version and it fails — #7f30ff on the dark surface measures about
					 * 2.3:1, under the 4.5:1 text minimum, so a darker category colour would be unread
					 * in dark mode.
					 */
					border: `1px solid ${alpha(accent, 0.45)}`,
					color: custom.text.main,
					fontSize: custom.fontSize.sm,
					fontWeight: custom.fontWeight.semibold,
					whiteSpace: 'nowrap',
					transition: 'background 150ms, border-color 150ms, color 150ms',
					/*
					 * Direct hover deepens it past the card's fill, so the button still answers the
					 * cursor once the card is already lit.
					 *
					 * getContrastText, not a hardcoded #fff: category colours are admin-editable at
					 * /admin/categories, so a pale one must not produce white-on-pale text.
					 */
					'&:hover': {
						background: darken(accent, 0.12),
						borderColor: darken(accent, 0.12),
						color: accentText,
					},
					/*
					 * Touch devices never hover, so the fill would never arrive — the primary action
					 * would sit neutral on every card on every phone. There it stays filled.
					 */
					'@media (hover: none)': {
						background: accent,
						borderColor: accent,
						color: accentText,
					},
					'&:focus-visible': {outline: `2px solid ${accent}`, outlineOffset: 2},
				}}
			>
				Try Free
			</Box>

			<Box
				component="button"
				type="button"
				data-ga="prepkit-details"
				aria-label={`Details for ${pkg.name}`}
				onClick={() => onDetails(pkg)}
				sx={{
					font: 'inherit',
					px: 2,
					py: 0.75,
					cursor: 'pointer',
					borderRadius: `${custom.radius.sm}px`,
					border: `1px solid ${custom.border.color}`,
					background: custom.bg.raised,
					color: custom.text.main,
					fontSize: custom.fontSize.sm,
					fontWeight: custom.fontWeight.semibold,
					whiteSpace: 'nowrap',
					transition: 'background 150ms, border-color 150ms',
					'&:hover': {background: custom.bg.raisedHover, borderColor: custom.border.strong},
					'&:focus-visible': {outline: `2px solid ${accent}`, outlineOffset: 2},
				}}
			>
				Details
			</Box>
		</Box>
	</Box>
)

function Category() {

	const {categoryToken} = useParams()
	const navigate = useNavigate()
	const theme = useTheme()
	const custom = theme.custom

	const [category, setCategory] = useState(null)
	const [packages, setPackages] = useState(null)
	const [filter, setFilter] = useState('')
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)

	useEffect(() => {
		if (!categoryToken) return

		let cancelled = false
		setLoading(true)
		setError(null)

		const fetchData = async () => {
			try {
				const response = await doGet(`/mini/shop/${categoryToken}`)
				if (!response.ok) throw new Error(`That category could not be found.`)
				const data = await response.json()
				if (cancelled) return
				setCategory(data.category)
				setPackages(data.packages || [])
			}
			catch (err) {
				log.error(err)
				if (!cancelled) setError(err.message || 'Something went wrong.')
			}
			finally {
				if (!cancelled) setLoading(false)
			}
		}

		fetchData()
		return () => { cancelled = true }
	}, [categoryToken])

	const shown = useMemo(() => {
		const term = filter.trim().toUpperCase()
		if (!term) return packages || []
		return (packages || []).filter(p => (p.name || '').toUpperCase().includes(term))
	}, [packages, filter])

	const accent = (category && category.color) || custom.indigo.primary
	const accentText = theme.palette.getContrastText(accent)

	/*
	 * Try Free goes to the EXISTING /try/:categoryToken/:packageToken screen (AuthGate.js:165).
	 *
	 * That screen already does the whole job: it bounces an unauthenticated visitor to
	 * /login?next=/try/... and comes back, then hands off to GET /mini/package/:token/try, which
	 * creates the trial prepkit and licenses server-side and redirects into the first question.
	 *
	 * So nothing here touches licensing. This is a navigate() to a route that already existed, which
	 * is also why the trial rules stay in one place rather than being re-decided on this page.
	 */
	const handleTry = (pkg) => {
		navigate(`/try/${categoryToken}/${pkg.token}`)
	}

	/*
	 * Details leaves for the marketing site, which owns the sales page for a PrepKit.
	 *
	 * Note there IS an in-app /prep/:categoryToken/:packageToken (PackageDetail) on the same path
	 * shape. Going out is deliberate per the brief — flagged in code-changes/165 rather than silently
	 * chosen, since the two are one character apart.
	 */
	const handleDetails = (pkg) => {
		window.location.href = `https://birdsy.ai/${categoryToken}/${pkg.token}`
	}

	return (
		<Container maxWidth="lg" sx={{pt: {xs: 3, md: 6}, pb: {xs: 4, md: 8}}}>
			<Helmet><title>{`Birdsy - ${category ? category.name : 'PrepKits'}`}</title></Helmet>

			{/* the link names where it goes, the same way the question screen's does (087) */}
			<Box
				onClick={() => navigate('/prep')}
				sx={{
					display: 'inline-flex',
					alignItems: 'center',
					gap: 0.5,
					mb: 2,
					cursor: 'pointer',
					fontSize: custom.fontSize.xs,
					fontWeight: custom.fontWeight.semibold,
					letterSpacing: '0.08em',
					textTransform: 'uppercase',
					color: custom.text.muted,
					'&:hover': {color: custom.link},
				}}
			>
				<ArrowBack sx={{fontSize: 16}}/>
				<Box component="span" sx={{lineHeight: 1}}>All categories</Box>
			</Box>

			{loading && (
				<Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, py: 6}}>
					<CircularProgress size={18} sx={{color: custom.text.muted}}/>
					<Typography sx={{fontSize: custom.fontSize.md, color: custom.text.muted}}>
						Loading PrepKits…
					</Typography>
				</Box>
			)}

			{!loading && error && (
				<Typography sx={{py: 6, fontSize: custom.fontSize.md, color: custom.status.danger}}>
					{error}
				</Typography>
			)}

			{!loading && !error && category && (
				<>
					{/* header: the category's own tile and name, the gradient's one appearance here */}
					<Box sx={{display: 'flex', alignItems: 'center', gap: 2, mb: {xs: 3, md: 4}}}>
						<CategoryIcon
							category={category}
							color={category.color}
							color2={category.color2}
							size={56}
							radius={custom.radius.lg}
						/>
						<Box sx={{minWidth: 0}}>
							<Typography sx={{
								fontSize: {xs: 24, md: 28},
								fontWeight: custom.fontWeight.bold,
								color: custom.text.main,
								lineHeight: 1.25,
							}}>
								{category.name}
							</Typography>
							<Typography sx={{mt: 0.25, fontSize: custom.fontSize.md, color: custom.text.muted}}>
								{packages.length} {packages.length === 1 ? 'PrepKit' : 'PrepKits'}
							</Typography>
						</Box>
					</Box>

					{/*
					  * A filter, because a category can hold fifty PrepKits — Real Estate is one per
					  * state. Shown only when there are enough for scanning to be the slower option.
					  */}
					{packages.length > 8 && (
						<TextField
							autoComplete="off"
							fullWidth
							size="small"
							name="package-filter"
							placeholder="Filter PrepKits"
							value={filter}
							onChange={(e) => setFilter(e.target.value)}
							sx={{
								mb: 3,
								maxWidth: 420,
								'& .MuiOutlinedInput-root': {
									borderRadius: `${custom.radius.sm}px`,
									background: custom.bg.surface,
									fontSize: custom.fontSize.md,
									'& fieldset': {borderColor: custom.border.color},
									'&:hover fieldset': {borderColor: custom.border.strong},
								},
							}}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<Search sx={{fontSize: 18, color: custom.text.muted}}/>
									</InputAdornment>
								),
							}}
						/>
					)}

					<Box sx={{
						display: 'grid',
						gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)'},
						gap: 2,
					}}>
						{shown.map(pkg => (
							<PrepKitCard
								key={`pkg-${pkg.id}`}
								pkg={pkg}
								accent={accent}
								accentText={accentText}
								custom={custom}
								onTry={handleTry}
								onDetails={handleDetails}
							/>
						))}
					</Box>

					{shown.length === 0 && (
						<Typography sx={{py: 4, fontSize: custom.fontSize.md, color: custom.text.muted}}>
							No PrepKits match “{filter}”.
						</Typography>
					)}
				</>
			)}
		</Container>
	)
}

export default Category
